import Config from "services/config/config";
import callWebService, { callTypes } from "services/utils/networks/network";
import { SITE_DASHBOARD_LISTING, SITE_DETAIL, SITE_DASHBOARD_EXPORT_CSV } from "./type";

const PER_PAGE = 15;

const normalizeDateToISO = (value) => {
  if (!value) return '';
  const v = String(value).trim();
  if (!v || v.toLowerCase() === 'invalid date') return '';
  // Already ISO yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  // UI uses dd-mm-yyyy (rc-datepicker returnFormat)
  const ddmmyyyy = v.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    return `${yyyy}-${mm}-${dd}`;
  }
  return v;
};

const isPresent = (value) => value != null && String(value).trim() !== '';

const clampPerPage = (value) => {
  if (!isPresent(value)) return PER_PAGE;
  const num = Number(value);
  if (!Number.isFinite(num)) return PER_PAGE;
  return Math.max(1, Math.min(100, Math.floor(num)));
};

const parseBooleanFlag = (value) => {
  if (value === true) return '1';
  if (value === false) return '0';
  if (!isPresent(value)) return '';
  const v = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y'].includes(v)) return '1';
  if (['0', 'false', 'no', 'n'].includes(v)) return '0';
  return '';
};

const buildSitesQuery = (queryObj, page) => {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("per_page", String(clampPerPage(queryObj?.per_page)));

  const includeDocUrls = parseBooleanFlag(queryObj?.include_document_urls);
  params.set("include_document_urls", includeDocUrls || "1");

  const includeTotal = parseBooleanFlag(queryObj?.include_total);
  params.set("include_total", includeTotal || "1");

  if (!queryObj) {
    return params.toString();
  }

  if (isPresent(queryObj.client_name)) params.set("client_name", String(queryObj.client_name).trim());
  if (isPresent(queryObj.site_no)) params.set("site_no", String(queryObj.site_no).trim());
  if (isPresent(queryObj.lead_no)) params.set("lead_no", String(queryObj.lead_no).trim());
  if (isPresent(queryObj.infra_project_id)) params.set("infra_project_id", String(queryObj.infra_project_id).trim());
  if (isPresent(queryObj.lead_type)) params.set("lead_type", String(queryObj.lead_type).trim());
  if (isPresent(queryObj.city)) params.set("city", String(queryObj.city).trim());

  // Backend expects `site_status`. Keep legacy `ops_status` fallback for existing UI/state.
  const siteStatus = isPresent(queryObj.site_status) ? queryObj.site_status : queryObj.ops_status;
  if (isPresent(siteStatus)) params.set("site_status", String(siteStatus).trim());

  if (isPresent(queryObj.contract_period)) params.set("contract_period", String(queryObj.contract_period).trim());

  // Dates: only include if explicitly provided by user.
  // Backend default: when all filters empty => last 1 month.
  // If any other filter is present and dates not passed => no date restriction.
  const createdOnISO = normalizeDateToISO(queryObj.created_on);
  if (createdOnISO) params.set("created_on", createdOnISO);

  const fromISO = normalizeDateToISO(queryObj.from_date);
  const toISO = normalizeDateToISO(queryObj.to_date);
  if (fromISO) params.set("from_date", fromISO);
  if (toISO) params.set("to_date", toISO);

  const onboardingISO = normalizeDateToISO(queryObj.onboarding_date);
  if (onboardingISO) params.set("onboarding_date", onboardingISO);
  const onboardingFromISO = normalizeDateToISO(queryObj.onboarding_from_date);
  const onboardingToISO = normalizeDateToISO(queryObj.onboarding_to_date);
  if (onboardingFromISO) params.set("onboarding_from_date", onboardingFromISO);
  if (onboardingToISO) params.set("onboarding_to_date", onboardingToISO);

  const liveISO = normalizeDateToISO(queryObj.live_date);
  if (liveISO) params.set("live_date", liveISO);
  const liveFromISO = normalizeDateToISO(queryObj.live_from_date);
  const liveToISO = normalizeDateToISO(queryObj.live_to_date);
  if (liveFromISO) params.set("live_from_date", liveFromISO);
  if (liveToISO) params.set("live_to_date", liveToISO);

  return params.toString();
};

const buildExportQuery = (queryObj) => {
  const params = new URLSearchParams();
  if (!queryObj) return params.toString();

  if (isPresent(queryObj.client_name)) params.set("client_name", String(queryObj.client_name).trim());
  if (isPresent(queryObj.site_no)) params.set("site_no", String(queryObj.site_no).trim());
  if (isPresent(queryObj.lead_no)) params.set("lead_no", String(queryObj.lead_no).trim());
  if (isPresent(queryObj.infra_project_id)) params.set("infra_project_id", String(queryObj.infra_project_id).trim());
  if (isPresent(queryObj.lead_type)) params.set("lead_type", String(queryObj.lead_type).trim());
  if (isPresent(queryObj.city)) params.set("city", String(queryObj.city).trim());

  const siteStatus = isPresent(queryObj.site_status) ? queryObj.site_status : queryObj.ops_status;
  if (isPresent(siteStatus)) params.set("site_status", String(siteStatus).trim());

  if (isPresent(queryObj.contract_period)) params.set("contract_period", String(queryObj.contract_period).trim());

  const createdOnISO = normalizeDateToISO(queryObj.created_on);
  if (createdOnISO) params.set("created_on", createdOnISO);
  const fromISO = normalizeDateToISO(queryObj.from_date);
  if (fromISO) params.set("from_date", fromISO);
  const toISO = normalizeDateToISO(queryObj.to_date);
  if (toISO) params.set("to_date", toISO);

  const onboardingISO = normalizeDateToISO(queryObj.onboarding_date);
  if (onboardingISO) params.set("onboarding_date", onboardingISO);
  const onboardingFromISO = normalizeDateToISO(queryObj.onboarding_from_date);
  if (onboardingFromISO) params.set("onboarding_from_date", onboardingFromISO);
  const onboardingToISO = normalizeDateToISO(queryObj.onboarding_to_date);
  if (onboardingToISO) params.set("onboarding_to_date", onboardingToISO);

  const liveISO = normalizeDateToISO(queryObj.live_date);
  if (liveISO) params.set("live_date", liveISO);
  const liveFromISO = normalizeDateToISO(queryObj.live_from_date);
  if (liveFromISO) params.set("live_from_date", liveFromISO);
  const liveToISO = normalizeDateToISO(queryObj.live_to_date);
  if (liveToISO) params.set("live_to_date", liveToISO);

  return params.toString();
};

const parseSitesListResponse = (res) => {
  const body = res?.data;
  if (!body) return null;

  if (Number(body.status) === 0 && body.data) {
    return body.data;
  }

  if (body.response?.data) {
    return body.response.data;
  }

  return null;
};

export const getSiteDashboardListing =
  (queryObj = null, page = 1) => async (dispatch) => {
    try {
      dispatch({
        type: SITE_DASHBOARD_LISTING,
        payload: {},
      });

      const query = buildSitesQuery(queryObj, page);
      const res = await callWebService(
        callTypes.get,
        "",
        `${Config.preSalesList.getSites}?${query}`
      );

      const payload = parseSitesListResponse(res);
      if (payload) {
        const rows = payload.data || payload.resultSets || [];
        dispatch({
          type: SITE_DASHBOARD_LISTING,
          payload: {
            resultSets: Array.isArray(rows) ? rows : [],
            current_page: payload.current_page,
            per_page: payload.per_page,
            total: payload.total,
            last_page: payload.last_page,
            from: payload.from,
            to: payload.to,
            has_more_pages: payload.has_more_pages,
            incomplete: payload.incomplete,
          },
        });
        return true;
      }
    } catch {
      dispatch({
        type: SITE_DASHBOARD_LISTING,
        payload: { resultSets: [] },
      });
    }
  };

const parseSiteDetailResponse = (res) => {
  const body = res?.data;
  if (!body) return null;
  if (Number(body.status) === 0 && body.data) return body.data;
  if (body.response?.data) return body.response.data;
  return null;
};

export const getSiteDetail = (siteId) => async (dispatch) => {
  try {
    dispatch({ type: SITE_DETAIL, payload: null });

    const res = await callWebService(
      callTypes.get,
      "",
      `${Config.preSalesList.getSiteDetail}?site_id=${siteId}`
    );

    const detail = parseSiteDetailResponse(res);
    if (detail) {
      dispatch({ type: SITE_DETAIL, payload: detail });
      return detail;
    }
    dispatch({ type: SITE_DETAIL, payload: null });
    return null;
  } catch {
    dispatch({ type: SITE_DETAIL, payload: null });
    return null;
  }
};

const parseExportSitesResponse = (res) => {
  const body = res?.data;
  if (!body) {
    return { success: false, message: "Export request failed" };
  }
  if (body instanceof Blob) {
    return {
      success: false,
      message: "Export could not be queued. Please try again later.",
    };
  }
  if (Number(body.status) === 0) {
    return {
      success: true,
      message: body.message || "Export queued successfully",
      data: body.data,
    };
  }
  return {
    success: false,
    message: body.message || "Export request failed",
  };
};

export const exportSitesCsv =
  (queryObj = null) =>
  async (dispatch) => {
    try {
      dispatch({ type: SITE_DASHBOARD_EXPORT_CSV, payload: { isExporting: true } });

      const query = buildExportQuery(queryObj);
      const url = query
        ? `${Config.preSalesList.exportSitesCsv}?${query}`
        : Config.preSalesList.exportSitesCsv;

      const res = await callWebService(callTypes.get, "", url, {
        headers: { Accept: "application/json" },
      });

      const result = parseExportSitesResponse(res);
      dispatch({ type: SITE_DASHBOARD_EXPORT_CSV, payload: { isExporting: false } });
      return result;
    } catch {
      dispatch({ type: SITE_DASHBOARD_EXPORT_CSV, payload: { isExporting: false } });
      return { success: false, message: "Export request failed" };
    }
  };
